import { useState, useRef } from "react";
import { Upload, Link as LinkIcon, FileAudio, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const MAX_BATCH = 10;
const AUDIO_EXTS = ["mp3", "wav", "m4a", "aac", "ogg", "flac"];

const validAudio = (file) => {
  const ext = file.name.split(".").pop().toLowerCase();
  if (!file.type.startsWith("audio/") && !AUDIO_EXTS.includes(ext)) {
    toast.error(`${file.name}: unsupported audio format`);
    return false;
  }
  if (file.size > 40 * 1024 * 1024) {
    toast.error(`${file.name}: too large (max 40MB)`);
    return false;
  }
  return true;
};

export const UploadPanel = ({ onAnalyzeFiles, onAnalyzeUrl, loading }) => {
  const [url, setUrl] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const inputRef = useRef(null);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(validAudio);
    if (!files.length) return;
    if (files.length > MAX_BATCH) toast.error(`Max ${MAX_BATCH} songs per batch — keeping the first ${MAX_BATCH}`);
    setSelectedFiles(files.slice(0, MAX_BATCH));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const submitFile = () => {
    if (!selectedFiles.length) return toast.error("Pick an audio file first");
    onAnalyzeFiles(selectedFiles);
  };

  const submitUrl = () => {
    if (!url.trim()) return toast.error("Paste a URL first");
    onAnalyzeUrl(url.trim());
  };

  const fileLabel = selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} songs selected`;
  const totalMb = selectedFiles.reduce((n, f) => n + f.size, 0) / 1024 / 1024;

  return (
    <div className="border border-white/10 bg-zinc-900/40 rounded-sm" data-testid="upload-panel">
      <div className="px-5 pt-4 pb-2 border-b border-white/5 flex items-center justify-between">
        <span className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          input // source_signal
        </span>
        <span className="font-mono-display text-[10px] tracking-[0.2em] uppercase text-emerald-500 whitespace-nowrap">
          [ ready ]
        </span>
      </div>

      <Tabs defaultValue="file" className="p-5">
        <TabsList className="bg-transparent border border-white/10 rounded-sm p-0 h-auto w-full grid grid-cols-2">
          <TabsTrigger
            value="file"
            data-testid="tab-file"
            className="rounded-none data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-500 data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 font-mono-display text-[11px] tracking-wider uppercase py-3 px-2 min-w-0"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5 shrink-0" /> Upload
          </TabsTrigger>
          <TabsTrigger
            value="url"
            data-testid="tab-url"
            className="rounded-none data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-500 data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 font-mono-display text-[11px] tracking-wider uppercase py-3 px-2 min-w-0"
          >
            <LinkIcon className="w-3.5 h-3.5 mr-1.5 shrink-0" /> URL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="file" className="mt-5">
          <div
            data-testid="dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`relative cursor-pointer border border-dashed transition-colors py-12 px-6 text-center rounded-sm ${
              dragActive ? "border-emerald-500 bg-emerald-500/5 glow-green" : "border-white/20 hover:border-emerald-500/60"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
              className="hidden"
              data-testid="file-input"
            />
            <FileAudio className="w-8 h-8 mx-auto text-emerald-500 mb-3" />
            <div className="font-mono-display text-sm text-white mb-1 break-words" data-testid="selected-files-label">
              {selectedFiles.length ? fileLabel : "Drop audio files here"}
            </div>
            <div className="font-mono-display text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              {selectedFiles.length
                ? `${totalMb.toFixed(2)} MB`
                : `mp3 · wav · m4a · flac · up to ${MAX_BATCH} songs`}
            </div>
          </div>
          <Button
            onClick={submitFile}
            disabled={loading || !selectedFiles.length}
            data-testid="analyze-file-btn"
            className="w-full mt-4 rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono-display text-xs tracking-[0.25em] uppercase font-bold py-6 border border-emerald-500 disabled:opacity-40"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Decoding...</>)
              : selectedFiles.length > 1 ? `Batch Decode ${selectedFiles.length} Songs` : "Initiate Decoding"}
          </Button>
        </TabsContent>

        <TabsContent value="url" className="mt-5 space-y-4">
          <div>
            <label className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2 block">
              youtube / soundcloud / direct audio url
            </label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              data-testid="url-input"
              className="rounded-none bg-zinc-950 border-white/10 focus:border-emerald-500 font-mono-display text-sm"
            />
            <p className="mt-2 text-[10px] font-mono-display text-zinc-500 tracking-wide">
              Works reliably with <span className="text-emerald-500">SoundCloud</span> and direct audio URLs. YouTube frequently blocks cloud servers — if that happens, download the mp3 and use the Upload tab.
            </p>
          </div>
          <Button
            onClick={submitUrl}
            disabled={loading || !url.trim()}
            data-testid="analyze-url-btn"
            className="w-full rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono-display text-xs tracking-[0.25em] uppercase font-bold py-6 border border-emerald-500 disabled:opacity-40"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching & Decoding...</>) : "Fetch & Decode"}
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
};
